import { motion } from "framer-motion";
import { ArrowUpRight, Github, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { projects } from "@/data/portfolio";
import { Reveal } from "@/lib/motion";
import { SectionHeader } from "@/components/About";
import { TID } from "@/constants/testIds";

function ProjectFrame({ index, domain, large }) {
  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-border bg-background ${large ? "h-56" : "h-40"}`}
    >
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="absolute -left-10 -top-16 h-48 w-48 rounded-full bg-primary/20 blur-3xl" />
      <div className="absolute inset-0 flex flex-col justify-between p-5">
        <span className="font-mono text-[0.68rem] uppercase tracking-[0.2em] text-muted-foreground">
          {domain}
        </span>
        <span className="font-display text-6xl font-extrabold text-foreground/10">{index}</span>
      </div>
    </div>
  );
}

export function Work() {
  return (
    <section id="work" data-testid={TID.work} className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
      <SectionHeader label="03 / work" title="Selected work, told as case studies." />

      <div className="md:hidden">
        <Accordion type="single" collapsible className="space-y-3">
          {projects.map((p) => (
            <AccordionItem
              key={p.id}
              value={p.id}
              className="overflow-hidden rounded-2xl border border-border bg-card/50 px-4"
            >
              <AccordionTrigger className="py-4 text-left no-underline hover:no-underline">
                <span className="flex flex-col items-start gap-1">
                  <span className="font-mono text-[0.68rem] text-muted-foreground">
                    {p.year} · <span className="text-primary">{p.status}</span>
                  </span>
                  <span className="font-display text-lg font-bold text-foreground">{p.name}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="space-y-4">
                  <ProjectFrame index={p.index} domain={p.domain} />
                  <p className="text-[0.96rem] leading-relaxed text-muted-foreground">{p.tagline}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {p.stack.slice(0, 4).map((s) => (
                      <span key={s} className="tag">
                        {s}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={`/work/${p.id}`}
                    data-testid={TID.projectOpen(p.id)}
                    className="group inline-flex w-fit items-center gap-2 font-mono text-xs text-primary link-underline"
                  >
                    Read case study <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <div className="hidden grid-cols-1 gap-5 md:grid lg:grid-cols-3">
        {projects.map((p, i) => (
          <Reveal key={p.id} delay={(i % 2) * 0.08}>
            <motion.article
              data-testid={TID.projectCard(p.id)}
              whileHover={{ y: -4 }}
              transition={{ type: "spring", stiffness: 250, damping: 22 }}
              className="group flex h-full flex-col gap-5 rounded-2xl border border-border bg-card/50 p-5 transition-colors hover:border-primary/30"
            >
              <ProjectFrame index={p.index} domain={p.domain} />
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2 font-mono text-[0.68rem] text-muted-foreground">
                    <span>{p.year}</span>
                    <span>·</span>
                    <span className="text-primary">{p.status}</span>
                    <span>·</span>
                    <span>{p.domain}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-foreground">{p.name}</h3>
                </div>
                {p.github && (
                  <a
                    href={p.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${p.name} source`}
                    className="text-muted-foreground transition-colors hover:text-primary"
                  >
                    <Github className="h-5 w-5" />
                  </a>
                )}
              </div>
              <p className="text-[0.96rem] leading-relaxed text-muted-foreground">{p.tagline}</p>
              <div className="flex flex-wrap gap-1.5">
                {p.stack.slice(0, 4).map((s) => (
                  <span key={s} className="tag">
                    {s}
                  </span>
                ))}
              </div>
              <Link
                to={`/work/${p.id}`}
                data-testid={TID.projectOpen(p.id)}
                className="group mt-auto inline-flex w-fit items-center gap-2 font-mono text-xs text-primary link-underline"
              >
                Read case study <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1} className="mt-10">
        <a
          href="https://github.com/jemsheena"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 font-mono text-sm text-muted-foreground link-underline hover:text-foreground"
        >
          More on GitHub <ArrowUpRight className="h-4 w-4" />
        </a>
      </Reveal>
    </section>
  );
}
